import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import Footer from '../components/Footer';

const TermsOfServicePage = () => {
  const lastUpdated = 'April 10, 2025';

  return (
    <>
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: { xs: 8, md: 10 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.25rem', md: '3rem' },
            }}
          >
            Terms of Service
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 1 }}
          >
            Last Updated: {lastUpdated}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            align="center"
            sx={{ maxWidth: '800px', mx: 'auto', mt: 3 }}
          >
            Please read these Terms of Service carefully before using the QResto AR menu platform.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            mb: 4,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using QResto's services, including our website, AR menu platform, and any other linked pages or services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). These Terms constitute a legal agreement between you and QResto Technologies Private Limited, a company registered in India ("QResto," "we," or "us").
          </Typography>
          <Typography variant="body1" paragraph>
            If you are using the Services on behalf of a business or other entity, you represent that you have the authority to bind that business or entity to these Terms, and "you" will refer to that business or entity.
          </Typography>
          <Typography variant="body1" paragraph>
            If you do not agree to these Terms, please do not use our Services.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            2. Description of Services
          </Typography>
          <Typography variant="body1" paragraph>
            QResto provides an augmented reality (AR) menu platform for restaurants and food service establishments. Our Services allow restaurants to create and display digital menus in augmented reality, accessible to customers via QR codes.
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to modify, update, or discontinue any aspect of our Services at any time. We will make reasonable efforts to notify you of significant changes to the Services that may affect your use.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            3. Your Account and Responsibilities
          </Typography>
          <Typography variant="body1" paragraph>
            To use certain features of our Services, you may need to create an account. You are responsible for:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Providing accurate, current, and complete information during the registration process."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Maintaining the security of your account credentials."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="All activities that occur under your account."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Promptly notifying QResto of any unauthorized use of your account or any other breach of security."
              />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph>
            QResto will not be liable for any loss or damage arising from your failure to comply with these obligations.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            4. Payment Terms
          </Typography>
          <Typography variant="body1" paragraph>
            QResto offers one-time setup fees as described on our Pricing page. By purchasing a plan, you agree to pay the fees indicated. All payments are non-refundable unless otherwise specified.
          </Typography>
          <Typography variant="body1" paragraph>
            Additional services, such as menu updates or maintenance packages, may be available at additional costs. We will clearly communicate any additional costs before you incur them.
          </Typography>
          <Typography variant="body1" paragraph>
            Prices may be subject to change. We will notify you of any price changes before they become effective.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            5. Content and Intellectual Property Rights
          </Typography>
          <Typography variant="body1" paragraph>
            The Services and all content, features, and functionality, including but not limited to all information, software, text, graphics, logos, images, and the design, selection, and arrangement thereof, are owned by QResto, its licensors, or other providers of such material and are protected by copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
            Your Content
          </Typography>
          <Typography variant="body1" paragraph>
            When you provide content to QResto, such as menu items, descriptions, and images ("Your Content"), you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, adapt, publish, translate, and display Your Content in connection with providing the Services.
          </Typography>
          <Typography variant="body1" paragraph>
            You represent and warrant that:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="You own or have the right to use and authorize QResto to use Your Content."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Your Content does not violate the rights of any third party, including intellectual property rights and privacy rights."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Your Content complies with these Terms and all applicable laws and regulations."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            6. Prohibited Uses
          </Typography>
          <Typography variant="body1" paragraph>
            You agree not to use the Services:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="In any way that violates any applicable law or regulation."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="To infringe upon or violate our intellectual property rights or the intellectual property rights of others."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="To transmit any material that is defamatory, offensive, or otherwise objectionable."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="To impersonate or attempt to impersonate QResto, a QResto employee, another user, or any other person or entity."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Services."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="To attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Services or any server, computer, or database connected to the Services."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            7. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph>
            IN NO EVENT WILL QRESTO, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SERVICES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
          </Typography>
          <Typography variant="body1" paragraph>
            THE FOREGOING DOES NOT AFFECT ANY LIABILITY WHICH CANNOT BE EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            8. Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            You agree to defend, indemnify, and hold harmless QResto, its affiliates, and their respective officers, directors, employees, contractors, agents, licensors, and suppliers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Services.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            9. Governing Law and Jurisdiction
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms shall be governed by and construed in accordance with the laws of India. Any legal suit, action, or proceeding arising out of, or related to, these Terms or the Services shall be instituted exclusively in the courts located in Bengaluru, Karnataka, India.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            10. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            QResto may terminate or suspend your access to all or part of the Services, without notice, for any conduct that QResto, in its sole discretion, believes violates these Terms or is harmful to other users of the Services, QResto, or third parties, or for any other reason.
          </Typography>
          <Typography variant="body1" paragraph>
            All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            11. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            QResto reserves the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </Typography>
          <Typography variant="body1" paragraph>
            By continuing to access or use our Services after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Services.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            12. Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            Questions or comments about the Services or these Terms may be directed to QResto at:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            Email: legal@qresto.com
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            Address: 123 Restaurant Ave, Food City, FC 12345, India
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            Phone: +91 98765 43210
          </Typography>
        </Paper>
      </Container>
      
      <Footer />
    </>
  );
};

export default TermsOfServicePage;